import { Box } from '@mui/material';
import PopupDialog from 'components/PopupDialog/PopupDialog';
import PopupNotification from 'components/PopupNotification/PopupNotification';
import { useContent } from 'contexts/Content/Content';
import { useSettings } from 'contexts/Settings/Settings';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useState } from 'react';

const PopupsContext = createContext<PopupsState>({} as PopupsState);

export const usePopups = () => useContext(PopupsContext);

export function withPopups(WrappedComponent: any) {
    return function PopupsWrappedComponent(props: any) {
        return (
            <PopupsContext.Consumer>{state => <WrappedComponent {...props} popups={state} />}</PopupsContext.Consumer>
        );
    };
}

export function PopupsProvider({ children }: PopupsProviderProps) {
    const [dialog, setDialog] = useState<PopupProps | undefined>(undefined);
    const [notification, setNotification] = useState<PopupProps | undefined>(undefined);
    const [queue, setQueue] = useState<PopupProps[]>([]);
    const content = useContent();
    const router = useRouter();
    const settings = useSettings();

    useEffect(() => {
        // Clear state on page change
        setDialog(undefined);
        setNotification(undefined);
        setQueue([]);
    }, [router.pathname]);

    useEffect(() => {
        // Get next dialog
        const newDialog = queue.find(item => item.type === 'dialog');
        setDialog(newDialog);

        if (!newDialog) {
            // Get next notification
            const newNotification = queue.find(item => item.type === 'notification');
            setNotification(newNotification);
        }
    }, [queue]);

    function get(name: string) {
        return content.popups[name];
    }

    function show(name: string, callback?: CallableFunction) {
        // Get popup
        const popup = get(name);
        if (settings.debugPopups || !popup) {
            return;
        }

        // Update queue
        setQueue(prevQueue => [
            ...prevQueue,
            {
                ...popup,
                callback,
                name,
            },
        ]);
    }

    function hide(name: string) {
        // Get popup
        const popup = queue.find(item => item.name === name);
        if (!popup) {
            return;
        }

        // Remove from queue
        setQueue(prevQueue => prevQueue.filter(p => p.name !== popup.name));

        // Go to link or fire callback
        if (popup.link) {
            void router.push(popup.link);
        } else {
            popup.callback?.();
        }
    }

    const state = {
        dialog,
        notification,
        queue,
        get,
        hide,
        show,
    };

    return (
        <PopupsContext.Provider value={state}>
            <PopupDialog />
            <PopupNotification />
            <Box aria-hidden={!!dialog}>{children}</Box>
        </PopupsContext.Provider>
    );
}
